import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { generateUUID } from '@famly/vet_ts-utils_generate-uuid';
import { ModalWrapper } from './modal-wrapper';
const modalContext = React.createContext(undefined);
/**
 * This is the context, which allows using the `useModal` hook.
 *
 * It should not be accessed directly, as the useModal hook is wrapping some of the important, e.g. memory-leak
 * preventing functionality, that is crucial for modals to not mess around with the whole app.
 *
 * Internally, the context adds a portal modal for each registered modal. This allows rendering any number of modals
 * at any place in the app.
 *
 * @param props Only contains the react children.
 * @returns The children wrapped in the modal context, having a portal modal sibling for each registered modal.
 */
export function ModalContext(props) {
    const modalRegistry = React.useRef([]);
    const registerModal = React.useCallback((modalProps) => {
        modalRegistry.current = [...modalRegistry.current, modalProps];
    }, []);
    const unregisterModal = React.useCallback((id) => {
        modalRegistry.current = modalRegistry.current.filter(modal => modal.id !== id);
    }, []);
    const [currentModalProps, setCurrentModalProps] = React.useState([]);
    const showModal = React.useCallback((id, modalProps) => {
        setCurrentModalProps(state => {
            const modalFromRegister = modalRegistry.current.find(modal => modal.id === id);
            if (!hasValue(modalFromRegister)) {
                return state;
            }
            const alreadyRenderedModalIndex = state.findIndex(modal => modal.id === modalFromRegister.id);
            if (alreadyRenderedModalIndex !== -1) {
                const newState = [...state];
                newState[alreadyRenderedModalIndex].props = modalProps;
                return newState;
            }
            else {
                return [
                    ...state,
                    {
                        ...modalFromRegister,
                        props: modalProps,
                    },
                ];
            }
        });
    }, []);
    const hideModal = React.useCallback((id) => {
        setCurrentModalProps(state => {
            return state.filter(modal => modal.id !== id);
        });
    }, []);
    return (_jsxs(modalContext.Provider, { value: React.useMemo(() => ({
            registerModal,
            unregisterModal,
            showModal,
            hideModal,
        }), [hideModal, registerModal, showModal, unregisterModal]), children: [currentModalProps.map((props, index) => (_jsx(ModalWrapper, { ...props, isTopMostModal: currentModalProps.length - 1 === index, handleHide: () => hideModal(props.id) }, props.id))), _jsx(React.Fragment, { children: props.children }, "unchanged")] }));
}
function useModalContext() {
    return React.use(modalContext);
}
/**
 * Hook to render a Modal.
 *
 * Internally uses the modal context to register and unregister modals.
 *
 * You should provide any static props to the modal via the `useModal` hook props, while contextual props, that
 * depend on the context, in which the `show` function returned by this hook is called, via the props put into that
 * function call.
 *
 * The generic parameter `<P>` refers to the dynamic props.
 *
 * @example
 *
 * ```typescript
 * // Some component to render in a modal
 * const HelloWorldComponent = (props: {hello: 'world'; bar: number[]}) => {
 *   return ...
 * }
 *
 * const {show, hide} = useModal({
 *      component: HelloWorldComponent
 * })
 *
 * // Types for `show` are inferred from the prop of HelloWorldComponent 🙌
 * show({hello: 'world', bar: [1, 2, 3]});
 * ```
 *
 * @param props props to register the modal. That includes the component to show, the portal modal props and some
 *      trimmed down styling options.
 * @returns an object containing the `show` function to render the modal and the `hide` function to hide it again
 * @deprecated use the new `useModal` hook from '@famly/mf_modals-dialogs_context
 *
 * @author Domi <dr@famly.co>
 * @author Emil <elh@famly.co>
 */
export function useModal(props) {
    const { hideModal, registerModal, showModal, unregisterModal } = useModalContext();
    const persistedId = React.useRef(generateUUID());
    React.useEffect(() => {
        const id = persistedId.current;
        registerModal({
            ...props,
            id,
        });
        return () => unregisterModal(id);
    }, [props, registerModal, unregisterModal]);
    const show = React.useCallback((modalProps) => showModal(persistedId.current, modalProps), [showModal]);
    const hide = React.useCallback(() => hideModal(persistedId.current), [hideModal]);
    return { show, hide };
}
